import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  WatchlistCategory,
  WatchlistStock,
  WatchlistRow,
  StockAssessment,
  PriceData,
  Trade,
  AccountSlug,
  TradeInput,
  PortfolioData,
  SnapshotData,
  Position,
} from '@/types'
import type { WatchlistCustomColumn, CustomValuesMap } from '@/types'
import type { SMAData } from '@/lib/watchlistComputations'
import { supabase } from '@/lib/supabase'
import { invokeEdgeFunction } from '@/lib/edgeFunctions'

const USER_ID = '00000000-0000-0000-0000-000000000000'

// Static category name map (slug → display name)
// Falls back to auto-formatting the slug if not in this map
const CATEGORY_NAMES: Record<string, string> = {
  'mega-cap-tech': 'Mega-Cap Tech',
  'ai---semiconductor': 'AI - Semiconductor',
  'ai---infrastructure': 'AI - Infrastructure',
  'ai---software-platform': 'AI - Software/Platform',
  'cybersecurity': 'Cybersecurity',
  'fintech---payments': 'Fintech - Payments',
  'healthcare---biotech': 'Healthcare - Biotech',
  'consumer---retail': 'Consumer - Retail',
  'energy---industrial': 'Energy - Industrial',
  'etfs---macro-exposure': 'ETFs - Macro Exposure',
  'reits---dividend': 'REITs - Dividend',
  'high-growth-speculative': 'High-Growth Speculative',
  'other': 'Other',
  // New granular categories
  'ai-automation-and-robotics': 'AI Automation & Robotics',
  'ai-data-center-and-gpu-hosting': 'AI Data Center & GPU Hosting',
  'ai-hardware-and-server-builders-the-buil': 'AI Hardware & Server Builders',
  'ai-software-and-data-platforms': 'AI Software & Data Platforms',
  'cloud-and-hyperscalers': 'Cloud & Hyperscalers',
  'consumer-ai-monetization': 'Consumer AI Monetization',
  'fintech-and-digital-finance': 'Fintech & Digital Finance',
  'memory-storage-of-data': 'Memory & Storage',
  'optical-and-silicon-photonics-the-speed': 'Optical & Silicon Photonics',
  'power-grid-and-infrastructure-the-plumbi': 'Power Grid & Infrastructure',
  'quantum-computing-high-risk': 'Quantum Computing (High Risk)',
  'semi-conductors-the-brain': 'Semiconductors',
}

/** Auto-format a category slug to a readable name if not in the map */
function formatCategoryName(slug: string): string {
  if (CATEGORY_NAMES[slug]) return CATEGORY_NAMES[slug]
  // Convert "ai-hardware-and-server-builders" → "Ai Hardware And Server Builders"
  return slug
    .replace(/-+/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\bAi\b/g, 'AI')
    .replace(/\bEtfs\b/g, 'ETFs')
}

interface AppState {
  // Navigation
  sideNavCollapsed: boolean;
  toggleSideNav: () => void;

  // VIX
  vixValue: number | null;
  vixLastUpdated: Date | null;
  vixIsStale: boolean;
  fetchVix: () => Promise<void>;

  // Watchlist
  categories: WatchlistCategory[];
  stocks: Record<string, WatchlistStock>;
  watchlistRows: Record<string, WatchlistRow>;
  assessments: Record<string, StockAssessment>;
  overrides: Record<string, Record<string, string>>;
  expandedCategories: Record<string, boolean>;
  smaData: Record<string, SMAData>;
  smaLoading: boolean;
  fetchWatchlistData: () => Promise<void>;
  updateWatchlistField: (stockId: string, field: string, value: any) => Promise<void>;
  setSMAData: (ticker: string, data: SMAData) => void;
  setSMALoading: (loading: boolean) => void;
  updateOverride: (stockId: string, field: string, value: string) => void;
  clearOverride: (stockId: string, field: string) => void;
  toggleCategory: (categoryId: string) => void;
  addStock: (ticker: string, name: string, categoryId: string) => Promise<void>;
  removeStock: (stockId: string) => Promise<void>;
  updateStockNote: (stockId: string, note: string) => Promise<void>;
  addCategory: (name: string) => Promise<void>;
  removeCategory: (categoryId: string) => Promise<void>;
  moveStock: (stockId: string, newCategoryId: string) => Promise<void>;

  // Custom Columns
  customColumns: WatchlistCustomColumn[];
  customValues: CustomValuesMap;
  fetchCustomColumns: () => Promise<void>;
  fetchCustomValues: () => Promise<void>;
  createCustomColumn: (name: string, type: string, options: string[], description?: string) => Promise<WatchlistCustomColumn | null>;
  updateCustomColumn: (id: string, updates: Partial<WatchlistCustomColumn>) => Promise<void>;
  deleteCustomColumn: (id: string) => Promise<void>;
  upsertCustomValue: (watchlistId: string, columnId: string, value: string) => Promise<void>;

  // Earnings Calendar
  earningsCalendar: Record<string, { date: string; hour: string; epsEstimate: number | null; fetchedAt: string } | null>;
  fetchEarningsCalendar: (tickers?: string[]) => Promise<void>;

  // Prices
  prices: Record<string, PriceData>;
  priceLastUpdated: Date | null;
  priceLastRefreshedAt: string | null; // ISO timestamp, persisted to localStorage
  priceProvider: string | null;
  priceFetchCount: number;
  earningsData: Record<string, { date: string | null; timing: string | null; epsEstimate: number | null }>;
  fetchPrices: (symbols: string[], includeHistory?: boolean, includeEarnings?: boolean) => Promise<void>;
  getPriceCacheAgeMinutes: () => number;
  isPriceCacheFresh: () => boolean;

  // Trades
  trades: Record<string, Trade[]>;
  fetchTrades: (account: AccountSlug) => Promise<void>;
  addTrade: (trade: TradeInput) => Promise<void>;
  updateTradeNote: (tradeId: string, note: string) => Promise<void>;

  // Portfolio
  portfolio: PortfolioData | null;
  fetchPortfolio: () => Promise<void>;

  // Snapshots
  availableSnapshots: string[];
  currentSnapshot: SnapshotData | null;
  fetchSnapshot: (date: string) => Promise<void>;
  fetchAvailableSnapshots: () => Promise<void>;

  // Positions
  positions: Record<string, Position[]>;
  fetchPositions: (accountSlug: string) => Promise<void>;
  createPosition: (accountSlug: string, position: Omit<Position, 'id'>) => Promise<Position | null>;
  updatePosition: (accountSlug: string, positionId: string, updates: Partial<Position>) => Promise<void>;
  deletePosition: (accountSlug: string, positionId: string) => Promise<void>;
  linkTradeToPosition: (tradeId: string, positionId: string) => Promise<void>;
  unlinkTradeFromPosition: (tradeId: string) => Promise<void>;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Navigation
      sideNavCollapsed: false,
      toggleSideNav: () => set(s => ({ sideNavCollapsed: !s.sideNavCollapsed })),

      // VIX
      vixValue: null,
      vixLastUpdated: null,
      vixIsStale: false,
      fetchVix: async () => {
        try {
          const data = await invokeEdgeFunction<Record<string, any>>('prices', {
            params: { vix: 'true' },
          })
          const vixData = data['VIX'] || data['^VIX'] || data['^GSPC'] || null
          set({
            vixValue: vixData?.currentPrice || null,
            vixLastUpdated: new Date(),
            vixIsStale: false,
          })
        } catch {
          set({ vixIsStale: true })
        }
      },

      // Watchlist
      categories: [],
      stocks: {},
      assessments: {},
      overrides: {},
      expandedCategories: {},
      watchlistRows: {},
      smaData: {},
      smaLoading: false,
      fetchWatchlistData: async () => {
        try {
          const { data: watchlistData, error: wErr } = await supabase.from('watchlist').select('*')
          if (wErr) throw wErr

          const watchlistRows = watchlistData || []

          // Reconstruct categories from stock category field
          const categoryMap = new Map<string, WatchlistCategory>()
          for (const row of watchlistRows) {
            const catSlug = row.category || 'other'
            if (!categoryMap.has(catSlug)) {
              categoryMap.set(catSlug, {
                id: catSlug,
                name: formatCategoryName(catSlug),
                displayOrder: categoryMap.size,
              })
            }
          }
          const categories: WatchlistCategory[] = Array.from(categoryMap.values())
            .sort((a, b) => a.displayOrder - b.displayOrder)

          // Build stocks map (for backward compat with CategorySection)
          const stocks: Record<string, WatchlistStock> = {}
          const wlRows: Record<string, WatchlistRow> = {}
          for (const row of watchlistRows) {
            stocks[row.id] = {
              id: row.id,
              ticker: row.ticker,
              name: row.name,
              categoryId: row.category || 'other',
              displayOrder: row.display_order || 0,
              notes: row.notes || undefined,
            }
            wlRows[row.id] = {
              id: row.id,
              ticker: row.ticker,
              name: row.name,
              category: row.category || 'other',
              display_order: row.display_order || 0,
              notes: row.notes || null,
              user_id: row.user_id || '',
              priority_tier: row.priority_tier ?? 2,
              own_score_3y: row.own_score_3y ?? 5,
              ai_exposure: row.ai_exposure || null,
              ai_stack_role: row.ai_stack_role || null,
              sub_category: row.sub_category || null,
              watchlist_status: row.watchlist_status || 'Research',
              investment_thesis: row.investment_thesis || null,
              leap_catalyst: row.leap_catalyst || null,
              hedge_candidate: row.hedge_candidate ?? false,
              iv_premium_level: row.iv_premium_level || null,
            }
          }

          // Fetch custom columns and values
          const [colRes, valRes] = await Promise.all([
            supabase.from('watchlist_custom_columns').select('*'),
            supabase.from('watchlist_custom_values').select('*'),
          ])

          const customColumns: WatchlistCustomColumn[] = (colRes.data || []).map((c: any) => ({
            id: c.id,
            name: c.name,
            type: c.type,
            options: c.options || [],
            displayOrder: c.display_order || 0,
            description: c.description || '',
          }))

          const customValues: CustomValuesMap = {}
          for (const v of valRes.data || []) {
            if (!customValues[v.watchlist_id]) customValues[v.watchlist_id] = {}
            customValues[v.watchlist_id][v.column_id] = v.value
          }

          set({ categories, stocks, watchlistRows: wlRows, customColumns, customValues })
        } catch (error) {
          console.error('Failed to fetch watchlist data:', error)
        }
      },
      updateWatchlistField: async (stockId, field, value) => {
        // Optimistic update in watchlistRows
        set(s => ({
          watchlistRows: {
            ...s.watchlistRows,
            [stockId]: { ...s.watchlistRows[stockId], [field]: value },
          },
        }))
        // Persist to Supabase
        const { error } = await supabase.from('watchlist').update({ [field]: value }).eq('id', stockId)
        if (error) {
          console.error(`Failed to update watchlist field ${field}:`, error)
          // Revert on error — refetch
          get().fetchWatchlistData()
        }
      },
      setSMAData: (ticker, data) => {
        set(s => ({ smaData: { ...s.smaData, [ticker]: data } }))
      },
      setSMALoading: (loading) => {
        set({ smaLoading: loading })
      },
      updateOverride: (stockId, field, value) => {
        // Optimistic update
        set(s => ({
          overrides: {
            ...s.overrides,
            [stockId]: { ...s.overrides[stockId], [field]: value },
          },
        }))
        // Persist to Supabase via upsert
        const state = get()
        const overrideData = { ...state.overrides[stockId], [field]: value }
        supabase.from('watchlist_assessments').upsert({
          watchlist_id: stockId,
          user_id: USER_ID,
          override_data: overrideData,
          [field.replace(/([A-Z])/g, '_$1').toLowerCase()]: value,
        }, { onConflict: 'watchlist_id' }).then(({ error }) => {
          if (error) console.error('Failed to update override:', error)
        })
      },
      clearOverride: (stockId, field) => {
        set(s => {
          const stockOverrides = { ...s.overrides[stockId] }
          delete stockOverrides[field]
          return { overrides: { ...s.overrides, [stockId]: stockOverrides } }
        })
        const state = get()
        const updatedOverrides = { ...state.overrides[stockId] }
        delete updatedOverrides[field]
        supabase.from('watchlist_assessments').upsert({
          watchlist_id: stockId,
          user_id: USER_ID,
          override_data: updatedOverrides,
          [field.replace(/([A-Z])/g, '_$1').toLowerCase()]: null,
        }, { onConflict: 'watchlist_id' }).then(({ error }) => {
          if (error) console.error('Failed to clear override:', error)
        })
      },

      toggleCategory: (categoryId) => {
        set(s => ({
          expandedCategories: {
            ...s.expandedCategories,
            [categoryId]: !s.expandedCategories[categoryId],
          },
        }))
      },
      addStock: async (ticker, name, categoryId) => {
        try {
          const { data, error } = await supabase.from('watchlist').insert({
            ticker: ticker.toUpperCase(),
            name,
            category: categoryId,
            user_id: USER_ID,
            display_order: 0,
          }).select().single()

          if (error) throw error

          const newStock: WatchlistStock = {
            id: data.id,
            ticker: ticker.toUpperCase(),
            name,
            categoryId,
            displayOrder: 0,
          }
          set(s => ({ stocks: { ...s.stocks, [newStock.id]: newStock } }))
        } catch (error) {
          console.error('Failed to add stock:', error)
          // Fallback: add locally
          const id = `stock_${Date.now()}`
          const newStock: WatchlistStock = { id, ticker: ticker.toUpperCase(), name, categoryId, displayOrder: 0 }
          set(s => ({ stocks: { ...s.stocks, [id]: newStock } }))
        }
      },
      removeStock: async (stockId) => {
        // Optimistic
        set(s => {
          const stocks = { ...s.stocks }
          delete stocks[stockId]
          return { stocks }
        })
        const { error } = await supabase.from('watchlist').delete().eq('id', stockId)
        if (error) console.error('Failed to remove stock:', error)
      },
      updateStockNote: async (stockId, note) => {
        set(s => ({
          stocks: { ...s.stocks, [stockId]: { ...s.stocks[stockId], notes: note } },
        }))
        const { error } = await supabase.from('watchlist').update({ notes: note }).eq('id', stockId)
        if (error) console.error('Failed to update stock note:', error)
      },
      addCategory: async (name) => {
        const id = `cat_${Date.now()}`
        set(s => ({
          categories: [...s.categories, { id, name, displayOrder: 999 }],
        }))
      },
      removeCategory: async (categoryId) => {
        set(s => ({
          categories: s.categories.filter(c => c.id !== categoryId),
        }))
      },
      moveStock: async (stockId, newCategoryId) => {
        set(s => ({
          stocks: { ...s.stocks, [stockId]: { ...s.stocks[stockId], categoryId: newCategoryId } },
        }))
        const { error } = await supabase.from('watchlist').update({ category: newCategoryId }).eq('id', stockId)
        if (error) console.error('Failed to move stock:', error)
      },

      // Custom Columns
      customColumns: [],
      customValues: {},
      fetchCustomColumns: async () => {
        try {
          const { data, error } = await supabase.from('watchlist_custom_columns').select('*')
          if (error) throw error
          const columns: WatchlistCustomColumn[] = (data || []).map((c: any) => ({
            id: c.id,
            name: c.name,
            type: c.type,
            options: c.options || [],
            displayOrder: c.display_order || 0,
            description: c.description || '',
          }))
          set({ customColumns: columns })
        } catch (e) {
          console.error('fetchCustomColumns failed:', e)
        }
      },
      fetchCustomValues: async () => {
        try {
          const { data, error } = await supabase.from('watchlist_custom_values').select('*')
          if (error) throw error
          const values: CustomValuesMap = {}
          for (const v of data || []) {
            if (!values[v.watchlist_id]) values[v.watchlist_id] = {}
            values[v.watchlist_id][v.column_id] = v.value
          }
          set({ customValues: values })
        } catch (e) {
          console.error('fetchCustomValues failed:', e)
        }
      },
      createCustomColumn: async (name, type, options, description) => {
        try {
          const { data, error } = await supabase.from('watchlist_custom_columns').insert({
            name,
            type,
            options,
            description: description || '',
            display_order: get().customColumns.length,
            user_id: USER_ID,
          }).select().single()

          if (error) throw error

          const col: WatchlistCustomColumn = {
            id: data.id,
            name: data.name,
            type: data.type,
            options: data.options || [],
            displayOrder: data.display_order || 0,
            description: data.description || '',
          }
          set(s => ({ customColumns: [...s.customColumns, col] }))
          return col
        } catch (e) {
          console.error('createCustomColumn failed:', e)
          throw e
        }
      },

      updateCustomColumn: async (id, updates) => {
        try {
          const dbUpdates: any = {}
          if (updates.name !== undefined) dbUpdates.name = updates.name
          if (updates.type !== undefined) dbUpdates.type = updates.type
          if (updates.options !== undefined) dbUpdates.options = updates.options
          if (updates.displayOrder !== undefined) dbUpdates.display_order = updates.displayOrder
          if (updates.description !== undefined) dbUpdates.description = updates.description

          const { data, error } = await supabase.from('watchlist_custom_columns')
            .update(dbUpdates)
            .eq('id', id)
            .select()
            .single()

          if (error) throw error

          set(s => ({
            customColumns: s.customColumns.map(c => c.id === id ? {
              ...c,
              ...updates,
              ...(data ? { name: data.name, type: data.type, options: data.options || [], displayOrder: data.display_order || 0, description: data.description || '' } : {}),
            } : c),
          }))
        } catch (e) {
          console.error('updateCustomColumn failed:', e)
          throw e
        }
      },
      deleteCustomColumn: async (id) => {
        try {
          const { error } = await supabase.from('watchlist_custom_columns').delete().eq('id', id)
          if (error) throw error
          set(s => ({
            customColumns: s.customColumns.filter(c => c.id !== id),
            customValues: Object.fromEntries(
              Object.entries(s.customValues).map(([wlId, colMap]) => [
                wlId,
                Object.fromEntries(Object.entries(colMap).filter(([cId]) => cId !== id)),
              ])
            ) as CustomValuesMap,
          }))
        } catch (e) {
          console.error('deleteCustomColumn failed:', e)
          throw e
        }
      },
      upsertCustomValue: async (watchlistId, columnId, value) => {
        // Optimistic update
        set(s => ({
          customValues: {
            ...s.customValues,
            [watchlistId]: { ...(s.customValues[watchlistId] || {}), [columnId]: value },
          },
        }))
        try {
          const { error } = await supabase.from('watchlist_custom_values').upsert({
            user_id: USER_ID,
            watchlist_id: watchlistId,
            column_id: columnId,
            value,
          }, { onConflict: 'user_id,watchlist_id,column_id' })
          if (error) console.error('upsertCustomValue failed:', error)
        } catch (e) {
          console.error('upsertCustomValue failed:', e)
        }
      },

      // Earnings Calendar
      earningsCalendar: {},
      fetchEarningsCalendar: async (tickers?: string[]) => {
        try {
          // 1. Read cached earnings from Supabase
          const { data, error } = await supabase.from('earnings_calendar').select('*')
          if (error) throw error
          const calendar: Record<string, any> = {}
          for (const row of data || []) {
            calendar[row.ticker] = {
              date: row.earnings_date,
              hour: row.hour || '',
              epsEstimate: row.eps_estimate,
              fetchedAt: row.updated_at || '',
            }
          }
          set({ earningsCalendar: calendar })

          // 2. If tickers provided, check which are missing or stale (>24h)
          if (tickers && tickers.length > 0) {
            const now = Date.now()
            const staleThreshold = 24 * 60 * 60 * 1000 // 24 hours
            const needsFetch = tickers.filter(t => {
              const cached = calendar[t]
              if (!cached || !cached.fetchedAt) return true
              const age = now - new Date(cached.fetchedAt).getTime()
              return age > staleThreshold
            })

            if (needsFetch.length > 0) {
              // 3. Call Edge Function for stale/missing tickers
              try {
                const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
                const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ''
                const res = await fetch(`${supabaseUrl}/functions/v1/earnings`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${supabaseKey}`,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({ symbols: needsFetch }),
                })
                if (res.ok) {
                  const result = await res.json()
                  const earnings = result.earnings || {}

                  // 4. Persist to Supabase
                  const rows = Object.entries(earnings)
                    .filter(([, val]) => val !== null)
                    .map(([ticker, val]: [string, any]) => ({
                      ticker,
                      earnings_date: val.date,
                      hour: val.hour || '',
                      eps_estimate: val.epsEstimate ?? null,
                    }))

                  if (rows.length > 0) {
                    await supabase
                      .from('earnings_calendar')
                      .upsert(rows, { onConflict: 'ticker' })
                  }

                  // 5. Update local state with new data
                  const updatedCalendar = { ...calendar }
                  for (const [ticker, val] of Object.entries(earnings)) {
                    if (val) {
                      updatedCalendar[ticker] = {
                        date: (val as any).date,
                        hour: (val as any).hour || '',
                        epsEstimate: (val as any).epsEstimate,
                        fetchedAt: new Date().toISOString(),
                      }
                    }
                  }
                  set({ earningsCalendar: updatedCalendar })
                }
              } catch (fetchErr) {
                console.warn('Earnings fetch from Finnhub failed:', fetchErr)
              }
            }
          }
        } catch (e) {
          console.error('fetchEarningsCalendar failed:', e)
        }
      },

      // Prices
      prices: {},
      priceLastUpdated: null,
      priceLastRefreshedAt: null,
      priceProvider: null,
      priceFetchCount: 0,
      earningsData: {},
      fetchPrices: async (symbols, includeHistory = false, includeEarnings = false) => {
        if (symbols.length === 0) return
        try {
          const params: Record<string, string> = { symbols: symbols.join(',') }
          if (includeHistory) params.includeHistory = 'true'
          if (includeEarnings) params.includeEarnings = 'true'

          const data = await invokeEdgeFunction<Record<string, any>>('prices', {
            params,
          })

          if (data.error) throw new Error(data.error)

          const prices: Record<string, PriceData> = {}
          const existingPrices = get().prices
          for (const [symbol, info] of Object.entries(data) as [string, any][]) {
            if (symbol.startsWith('_')) continue // Skip _meta, _earnings keys
            if (!info || typeof info !== 'object') continue
            const existing = existingPrices[symbol]
            prices[symbol] = {
              currentPrice: info.currentPrice || 0,
              // Preserve existing historical values if new response doesn't include them
              sixMonthLow: info.sixMonthLow || (existing?.sixMonthLow) || null,
              sixMonthHigh: info.sixMonthHigh || (existing?.sixMonthHigh) || null,
              fiftyTwoWeekHigh: info.fiftyTwoWeekHigh || (existing?.fiftyTwoWeekHigh) || null,
              sixMonthDiscount: info.sixMonthDiscount ?? (existing?.sixMonthDiscount) ?? null,
              forwardPe: info.forwardPe ?? (existing?.forwardPe) ?? null,
              pegRatio: info.pegRatio ?? (existing?.pegRatio) ?? null,
              priceSales: info.priceSales ?? (existing?.priceSales) ?? null,
              lastUpdated: new Date(info.lastUpdated || Date.now()),
              isStale: false,
            }
          }

          // Extract metadata
          const meta = data._meta || {}
          const provider = meta.provider || null
          const fetchCount = Object.keys(prices).length

          // Extract earnings if present
          const earningsData: Record<string, { date: string | null; timing: string | null; epsEstimate: number | null }> = {}
          if (data._earnings) {
            for (const [symbol, info] of Object.entries(data._earnings) as [string, any][]) {
              earningsData[symbol] = {
                date: info.date || null,
                timing: info.timing || null,
                epsEstimate: info.epsEstimate ?? null,
              }
            }
          }

          const now = new Date().toISOString()
          set(s => ({
            prices: { ...s.prices, ...prices },
            priceLastUpdated: new Date(),
            priceLastRefreshedAt: now,
            priceProvider: provider,
            priceFetchCount: fetchCount,
            ...(Object.keys(earningsData).length > 0 ? { earningsData: { ...s.earningsData, ...earningsData } } : {}),
          }))
        } catch (err) {
          console.error('Failed to fetch prices:', err)
          set(s => {
            const stale: Record<string, PriceData> = {}
            for (const [k, v] of Object.entries(s.prices)) {
              stale[k] = { ...v, isStale: true }
            }
            return { prices: stale }
          })
        }
      },
      getPriceCacheAgeMinutes: () => {
        const lastRefreshed = get().priceLastRefreshedAt
        if (!lastRefreshed) return Infinity
        const age = Date.now() - new Date(lastRefreshed).getTime()
        return Math.floor(age / 60_000)
      },
      isPriceCacheFresh: () => {
        return get().getPriceCacheAgeMinutes() < 360 // 6 hours
      },

      // Trades
      trades: {},
      fetchTrades: async (account) => {
        try {
          const { data, error } = await supabase.from('trades')
            .select('*')
            .eq('account_slug', account)

          if (error) throw error

          const trades: Trade[] = (data || []).map((row: any) => ({
            id: row.id,
            accountSlug: row.account_slug,
            ticker: row.ticker,
            strategy: row.strategy,
            tradeDate: row.trade_date || row.buy_date || '',
            strikePrice: row.strike_price,
            premium: row.premium,
            expirationDate: row.exp_date || row.expiration_date || '',
            quantity: row.qty || row.quantity || 0,
            status: row.status || 'open',
            closeDate: row.close_date || undefined,
            closePremium: row.close_premium || undefined,
            pnl: row.net_position || row.pnl || undefined,
            notes: row.notes || undefined,
            createdAt: row.created_at || '',
            updatedAt: row.updated_at || '',
          }))

          set(s => ({
            trades: { ...s.trades, [account]: trades },
          }))
        } catch (error) {
          console.error('Failed to fetch trades:', error)
        }
      },
      addTrade: async (trade) => {
        const newTrade: Trade = {
          id: `trade_${Date.now()}`,
          accountSlug: trade.accountSlug,
          ticker: trade.ticker.toUpperCase(),
          strategy: trade.strategy,
          tradeDate: trade.tradeDate,
          strikePrice: trade.strikePrice,
          premium: trade.premium,
          expirationDate: trade.expirationDate,
          quantity: trade.quantity,
          status: 'open',
          notes: trade.notes,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
        // Optimistic update
        set(s => ({
          trades: {
            ...s.trades,
            [trade.accountSlug]: [newTrade, ...(s.trades[trade.accountSlug] || [])],
          },
        }))
        // Persist
        const { error } = await supabase.from('trades').insert({
          account_slug: trade.accountSlug,
          ticker: trade.ticker.toUpperCase(),
          strategy: trade.strategy,
          trade_date: trade.tradeDate,
          strike_price: trade.strikePrice,
          premium: trade.premium,
          exp_date: trade.expirationDate,
          qty: trade.quantity,
          status: 'open',
          notes: trade.notes,
          user_id: USER_ID,
        })
        if (error) console.error('Failed to add trade:', error)
      },

      updateTradeNote: async (tradeId, note) => {
        set(s => {
          const newTrades = { ...s.trades }
          for (const [account, trades] of Object.entries(newTrades)) {
            newTrades[account] = trades.map(t =>
              t.id === tradeId ? { ...t, notes: note, updatedAt: new Date().toISOString() } : t
            )
          }
          return { trades: newTrades }
        })
        const { error } = await supabase.from('trades').update({ notes: note, updated_at: new Date().toISOString() }).eq('id', tradeId)
        if (error) console.error('Failed to update trade note:', error)
      },

      // Portfolio (computed from trades)
      portfolio: null,
      fetchPortfolio: async () => {
        // TODO: compute from trades
        console.log('[Store] fetchPortfolio')
      },

      // Snapshots
      availableSnapshots: [],
      currentSnapshot: null,
      fetchAvailableSnapshots: async () => {
        try {
          const { data, error } = await supabase.from('watchlist_snapshots')
            .select('snapshot_date')
            .order('snapshot_date', { ascending: false })
          if (error) throw error
          set({ availableSnapshots: (data || []).map((r: any) => r.snapshot_date) })
        } catch (e) {
          console.error('Failed to fetch snapshots:', e)
        }
      },
      fetchSnapshot: async (date) => {
        try {
          const { data, error } = await supabase.from('watchlist_snapshots')
            .select('*')
            .eq('snapshot_date', date)
          if (error) throw error
          if (data && data.length > 0) {
            set({ currentSnapshot: { snapshotDate: date, stocks: data[0].data || [] } })
          }
        } catch (e) {
          console.error('Failed to fetch snapshot:', e)
        }
      },

      // Positions
      positions: {},
      fetchPositions: async (accountSlug) => {
        try {
          const { data, error } = await supabase.from('positions')
            .select('*')
            .eq('account_slug', accountSlug)

          if (error) throw error

          const positions: Position[] = (data || []).map((row: any) => ({
            id: row.id,
            accountSlug: row.account_slug,
            ticker: row.ticker,
            quantity: row.quantity || row.qty || 0,
            costBasis: row.cost_basis || 0,
            acquisitionDate: row.assigned_date || row.acquisition_date || '',
            sourceTradeId: row.linked_trade_id || row.source_trade_id || '',
            status: row.status || 'open',
            closedDate: row.sold_date || row.closed_date || undefined,
            notes: row.metadata?.notes || row.notes || undefined,
          }))

          set(s => ({
            positions: { ...s.positions, [accountSlug]: positions },
          }))
        } catch (error) {
          console.error('Failed to fetch positions:', error)
        }
      },
      createPosition: async (accountSlug, position) => {
        try {
          const { data, error } = await supabase.from('positions').insert({
            account_slug: accountSlug,
            ticker: position.ticker,
            quantity: position.quantity,
            cost_basis: position.costBasis,
            assigned_date: position.acquisitionDate,
            linked_trade_id: position.sourceTradeId,
            status: position.status || 'open',
            metadata: {},
            user_id: USER_ID,
          }).select().single()

          if (error) throw error

          const newPosition: Position = {
            id: data.id,
            accountSlug: data.account_slug,
            ticker: data.ticker,
            quantity: data.quantity || 0,
            costBasis: data.cost_basis || 0,
            acquisitionDate: data.assigned_date || '',
            sourceTradeId: data.linked_trade_id || '',
            status: data.status || 'open',
            closedDate: data.sold_date || undefined,
            notes: data.metadata?.notes || undefined,
          }

          set(s => ({
            positions: {
              ...s.positions,
              [accountSlug]: [...(s.positions[accountSlug] || []), newPosition],
            },
          }))
          return newPosition
        } catch (error) {
          console.error('Failed to create position:', error)
          return null
        }
      },

      updatePosition: async (accountSlug, positionId, updates) => {
        try {
          const dbUpdates: any = {}
          if (updates.ticker !== undefined) dbUpdates.ticker = updates.ticker
          if (updates.quantity !== undefined) dbUpdates.quantity = updates.quantity
          if (updates.costBasis !== undefined) dbUpdates.cost_basis = updates.costBasis
          if (updates.acquisitionDate !== undefined) dbUpdates.assigned_date = updates.acquisitionDate
          if (updates.status !== undefined) dbUpdates.status = updates.status
          if (updates.closedDate !== undefined) dbUpdates.sold_date = updates.closedDate
          if (updates.notes !== undefined) dbUpdates.metadata = { notes: updates.notes }

          const { data, error } = await supabase.from('positions')
            .update(dbUpdates)
            .eq('id', positionId)
            .select()
            .single()

          if (error) throw error

          const updated: Position = {
            id: data.id,
            accountSlug: data.account_slug,
            ticker: data.ticker,
            quantity: data.quantity || 0,
            costBasis: data.cost_basis || 0,
            acquisitionDate: data.assigned_date || '',
            sourceTradeId: data.linked_trade_id || '',
            status: data.status || 'open',
            closedDate: data.sold_date || undefined,
            notes: data.metadata?.notes || undefined,
          }

          set(s => ({
            positions: {
              ...s.positions,
              [accountSlug]: (s.positions[accountSlug] || []).map(p =>
                p.id === positionId ? updated : p
              ),
            },
          }))
        } catch (error) {
          console.error('Failed to update position:', error)
        }
      },
      deletePosition: async (accountSlug, positionId) => {
        // Optimistic delete
        set(s => ({
          positions: {
            ...s.positions,
            [accountSlug]: (s.positions[accountSlug] || []).filter(p => p.id !== positionId),
          },
        }))
        const { error } = await supabase.from('positions').delete().eq('id', positionId)
        if (error) console.error('Failed to delete position:', error)
      },
      linkTradeToPosition: async (tradeId, positionId) => {
        const { error } = await supabase.from('trades')
          .update({ position_id: positionId })
          .eq('id', tradeId)
        if (error) console.error('Failed to link trade to position:', error)
      },
      unlinkTradeFromPosition: async (tradeId) => {
        const { error } = await supabase.from('trades')
          .update({ position_id: null })
          .eq('id', tradeId)
        if (error) console.error('Failed to unlink trade from position:', error)
      },
    }),
    {
      name: 'trading-journal-store',
      partialize: (state) => ({
        sideNavCollapsed: state.sideNavCollapsed,
        expandedCategories: state.expandedCategories,
        // Persist price cache so it survives page reloads (6h TTL checked on use)
        prices: state.prices,
        priceLastRefreshedAt: state.priceLastRefreshedAt,
        priceProvider: state.priceProvider,
        priceFetchCount: state.priceFetchCount,
        earningsData: state.earningsData,
      }),
    }
  )
)
